import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Clock,
  Gauge,
  GitBranch,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import { Link } from "react-router";

import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import { defaultScoringRules } from "../root/defaultConfiguration";
import { EmptyState } from "../misc/EmptyState";
import type { CrmDataProvider } from "../providers/types";
import type {
  AssigneeRule,
  BranchCondition,
  Contact,
  Sale,
  ScoringRule,
  Tag,
  WorkflowAction,
  WorkflowBranch,
  WorkflowDef,
  WorkflowDelay,
  WorkflowStep,
} from "../types";
import { flattenWorkflowActions } from "../types";
import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import { shortDate, simulateWorkflowRuns } from "./assetActivity";
import { useLiveRuns } from "./useLiveEnrollments";

/**
 * Workflows - trigger → action automations (score changes, owner changes,
 * tags, sequence enrollment, tasks, emails) plus the native lead-scoring
 * rules they feed on. Definitions save per tenant now; the Phase 4
 * automation engine executes them at deployment (see docs/decisions.md).
 */

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

const TRIGGERS: {
  id: WorkflowDef["trigger"]["type"];
  label: string;
  paramLabel?: string;
  paramKind?: "stage" | "days" | "threshold" | "tag";
}[] = [
  { id: "contact_created", label: "Contact is created" },
  { id: "form_submitted", label: "Form is submitted" },
  { id: "deal_created", label: "Deal is created" },
  { id: "deal_stage_changed", label: "Deal moves to stage", paramLabel: "Stage", paramKind: "stage" },
  { id: "email_replied", label: "Contact replies to an email" },
  { id: "score_crossed", label: "Lead score crosses", paramLabel: "Threshold", paramKind: "threshold" },
  { id: "tag_added", label: "Tag is added", paramLabel: "Tag name", paramKind: "tag" },
  { id: "no_activity", label: "No activity for N days", paramLabel: "Days", paramKind: "days" },
];

const ACTIONS: { id: WorkflowAction["type"]; label: string }[] = [
  { id: "adjust_score", label: "Adjust lead score" },
  { id: "set_owner", label: "Change owner" },
  { id: "add_tag", label: "Add a tag" },
  { id: "enroll_sequence", label: "Enroll in sequence" },
  { id: "create_task", label: "Create a task" },
  { id: "send_email", label: "Send an email" },
  { id: "notify_user", label: "Notify a teammate" },
];

/** Personalization tokens the engine substitutes at execution time. */
const TOKEN_HINT = "Tokens: {{first_name}} {{last_name}} {{company}}";

const assigneeLabel = (rule: AssigneeRule | undefined, sales: Sale[]): string => {
  const mode = rule?.mode ?? "owner";
  if (mode === "owner") return "contact's owner";
  if (mode === "round_robin") {
    const n = rule?.poolIds?.length ?? 0;
    return n > 0 ? `round-robin (${n} people)` : "round-robin (everyone)";
  }
  const s = sales.find((x) => String(x.id) === rule?.userId);
  return s ? `${s.first_name} ${s.last_name}` : "pick a person";
};

const triggerDef = (t: WorkflowDef["trigger"]["type"]) =>
  TRIGGERS.find((x) => x.id === t)!;
const actionDef = (t: WorkflowAction["type"]) =>
  ACTIONS.find((x) => x.id === t)!;

// --- If/then branches ------------------------------------------------------

const CONDITIONS: { id: BranchCondition["kind"]; label: string }[] = [
  { id: "score_gte", label: "Lead score is at least" },
  { id: "score_lt", label: "Lead score is below" },
  { id: "has_tag", label: "Has the tag" },
  { id: "not_has_tag", label: "Does NOT have the tag" },
  { id: "unowned", label: "Has no owner" },
  { id: "owner_is", label: "Owner is" },
  { id: "source_is", label: "Original source is" },
];

const SOURCE_CHANNELS = ["organic", "paid", "social", "email", "direct", "referral"];

const describeCondition = (c: BranchCondition, sales: Sale[]): string => {
  switch (c.kind) {
    case "score_gte": return `score ≥ ${c.value ?? "?"}`;
    case "score_lt": return `score < ${c.value ?? "?"}`;
    case "has_tag": return `has tag "${c.value ?? "?"}"`;
    case "not_has_tag": return `no tag "${c.value ?? "?"}"`;
    case "unowned": return "has no owner";
    case "owner_is": {
      const s = sales.find((x) => String(x.id) === c.value);
      return `owner is ${s ? `${s.first_name} ${s.last_name}` : "(pick)"}`;
    }
    case "source_is": return `source is ${c.value ?? "(pick)"}`;
  }
};

/** How deep branches may nest in the builder - keeps flows readable. */
const MAX_BRANCH_DEPTH = 2;

const newAction = (): WorkflowAction => ({
  id: nextUid(),
  type: "create_task",
  params: { value: "" },
  config: { assignee: { mode: "owner" }, dueInDays: 0 },
});

const newBranch = (): WorkflowBranch => ({
  id: nextUid(),
  type: "branch",
  condition: { kind: "score_gte", value: "50" },
  then: [],
  otherwise: [],
});

const newDelay = (): WorkflowDelay => ({
  id: nextUid(),
  type: "delay",
  days: 3,
});

const ConditionEditor = ({
  value,
  onChange,
  sales,
  tags,
  datalistId,
}: {
  value: BranchCondition;
  onChange: (c: BranchCondition) => void;
  sales: Sale[];
  tags: Tag[];
  datalistId: string;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <Select
      value={value.kind}
      onValueChange={(kind: BranchCondition["kind"]) => onChange({ kind, value: "" })}
    >
      <SelectTrigger className="h-8 w-52 text-sm"><SelectValue /></SelectTrigger>
      <SelectContent>
        {CONDITIONS.map((c) => (
          <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {(value.kind === "score_gte" || value.kind === "score_lt") && (
      <Input
        type="number"
        className="h-8 w-20 text-sm"
        value={value.value ?? ""}
        onChange={(e) => onChange({ ...value, value: e.target.value })}
      />
    )}
    {(value.kind === "has_tag" || value.kind === "not_has_tag") && (
      <>
        <Input
          className="h-8 w-36 text-sm"
          list={datalistId}
          placeholder="tag"
          value={value.value ?? ""}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
        <datalist id={datalistId}>
          {tags.map((t) => (
            <option key={String(t.id)} value={t.name} />
          ))}
        </datalist>
      </>
    )}
    {value.kind === "owner_is" && (
      <Select
        value={value.value ?? ""}
        onValueChange={(v) => onChange({ ...value, value: v })}
      >
        <SelectTrigger className="h-8 w-40 text-sm"><SelectValue placeholder="who…" /></SelectTrigger>
        <SelectContent>
          {sales.map((s) => (
            <SelectItem key={String(s.id)} value={String(s.id)}>
              {s.first_name} {s.last_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
    {value.kind === "source_is" && (
      <Select
        value={value.value ?? ""}
        onValueChange={(v) => onChange({ ...value, value: v })}
      >
        <SelectTrigger className="h-8 w-36 text-sm"><SelectValue placeholder="channel…" /></SelectTrigger>
        <SelectContent>
          {SOURCE_CHANNELS.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
  </div>
);

// --- Step editors (actions + if/then branches, recursive) ------------------

interface StepEditorDeps {
  sales: Sale[];
  tags: Tag[];
  crmConfig: ConfigurationContextValue;
}

const ActionCard = ({
  action: a,
  onChange,
  onRemove,
  deps,
}: {
  action: WorkflowAction;
  onChange: (next: WorkflowAction) => void;
  onRemove: () => void;
  deps: StepEditorDeps;
}) => {
  const { sales, tags, crmConfig } = deps;
  const c = a.config ?? {};
  const row = "flex flex-wrap items-center gap-2 text-sm";
  const lbl = "text-xs text-muted-foreground w-20 shrink-0";
  const patchConfig = (patch: NonNullable<WorkflowAction["config"]>) =>
    onChange({ ...a, config: { ...a.config, ...patch } });
  const setValue = (value: string) =>
    onChange({ ...a, params: { ...a.params, value } });
  // Auto-expanded when an override is already set, so an existing one is
  // never hidden - collapsed by default otherwise, since the template's own
  // subject is what sends in the common case.
  const [showSubjectOverride, setShowSubjectOverride] = useState(Boolean(c.subjectOverride));

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-2.5 bg-card">
      <div className="flex items-center gap-2">
        <Select
          value={a.type}
          onValueChange={(type: WorkflowAction["type"]) =>
            onChange({ id: a.id, type, params: {}, config: {} })
          }
        >
          <SelectTrigger className="h-8 w-48 text-sm font-medium"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACTIONS.map((x) => (
              <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          aria-label="Remove action"
          onClick={onRemove}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {a.type === "adjust_score" && (
        <div className={row}>
          <span className={lbl}>Points (±)</span>
          <Input
            type="number"
            className="h-8 w-24 text-sm"
            value={a.params?.value ?? ""}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
      )}

      {a.type === "set_owner" && (
        <>
          <div className={row}>
            <span className={lbl}>Assign to</span>
            <AssigneePicker
              value={c.assignee ?? { mode: "user" }}
              onChange={(assignee) => patchConfig({ assignee })}
              sales={sales}
              allowOwner={false}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={c.onlyIfUnowned === true}
              onCheckedChange={(onlyIfUnowned) => patchConfig({ onlyIfUnowned })}
            />
            Only when the contact has no owner yet
          </label>
        </>
      )}

      {a.type === "add_tag" && (
        <div className={row}>
          <span className={lbl}>Tag</span>
          <Input
            className="h-8 w-44 text-sm"
            list={`tags-${a.id}`}
            placeholder="pick or type a tag"
            value={a.params?.value ?? ""}
            onChange={(e) => setValue(e.target.value)}
          />
          <datalist id={`tags-${a.id}`}>
            {tags.map((t) => (
              <option key={String(t.id)} value={t.name} />
            ))}
          </datalist>
        </div>
      )}

      {a.type === "enroll_sequence" && (
        <>
          <div className={row}>
            <span className={lbl}>Sequence</span>
            <Select value={a.params?.value ?? ""} onValueChange={setValue}>
              <SelectTrigger className="h-8 w-56 text-sm"><SelectValue placeholder="pick a sequence…" /></SelectTrigger>
              <SelectContent>
                {(crmConfig.sequences ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* "Send as" and "skip if enrolled" used to be options here, but
              neither could take effect (P6): sends always use the workspace's
              marketing sender, and the database never lets a contact into the
              same sequence twice at once. */}
          <p className="text-xs text-muted-foreground">
            Emails send from this workspace's marketing sender. Contacts already in
            this sequence are skipped.
          </p>
        </>
      )}

      {a.type === "create_task" && (
        <>
          <div className={row}>
            <span className={lbl}>Title</span>
            <Input
              className="h-8 flex-1 min-w-52 text-sm"
              placeholder="Call {{first_name}} about the new lead"
              value={a.params?.value ?? ""}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className={row}>
            <span className={lbl}>Assign to</span>
            <AssigneePicker
              value={c.assignee}
              onChange={(assignee) => patchConfig({ assignee })}
              sales={sales}
            />
          </div>
          <div className={row}>
            <span className={lbl}>Due</span>
            <Input
              type="number"
              min={0}
              className="h-8 w-20 text-sm"
              value={c.dueInDays ?? 0}
              onChange={(e) =>
                patchConfig({ dueInDays: Math.max(0, Number(e.target.value) || 0) })
              }
            />
            <span className="text-xs text-muted-foreground">days after the run</span>
            <span className="text-xs text-muted-foreground">Type</span>
            <Select
              value={c.taskType ?? crmConfig.taskTypes?.[0]?.value ?? "Call"}
              onValueChange={(taskType) => patchConfig({ taskType })}
            >
              <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(crmConfig.taskTypes ?? []).map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">{TOKEN_HINT}</p>
        </>
      )}

      {a.type === "send_email" && (
        <>
          <div className={row}>
            <span className={lbl}>Template</span>
            <Select value={a.params?.value ?? ""} onValueChange={setValue}>
              <SelectTrigger className="h-8 w-56 text-sm"><SelectValue placeholder="pick a template…" /></SelectTrigger>
              <SelectContent>
                {(crmConfig.emailTemplates ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link
              to="/marketing?tab=emails"
              className="text-[11px] text-muted-foreground underline hover:no-underline"
            >
              edit templates
            </Link>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Sends from this workspace's approved marketing address (
            <Link to="/marketing?tab=emails" className="underline hover:no-underline">
              set in Emails
            </Link>
            ) - not from any specific person.
          </p>
          <button
            type="button"
            onClick={() => setShowSubjectOverride((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground w-fit"
          >
            {showSubjectOverride ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Advanced: override this step's subject
          </button>
          {showSubjectOverride && (
            <>
              <div className={row}>
                <span className={lbl}>Subject</span>
                <Input
                  className="h-8 flex-1 min-w-52 text-sm"
                  placeholder="(template's own subject unless overridden)"
                  value={c.subjectOverride ?? ""}
                  onChange={(e) => patchConfig({ subjectOverride: e.target.value })}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">{TOKEN_HINT}</p>
            </>
          )}
        </>
      )}

      {a.type === "notify_user" && (
        <>
          <div className={row}>
            <span className={lbl}>Notify</span>
            <AssigneePicker
              value={c.assignee ?? { mode: "user" }}
              onChange={(assignee) => patchConfig({ assignee })}
              sales={sales}
            />
          </div>
          <div className={row}>
            <span className={lbl}>Message</span>
            <Input
              className="h-8 flex-1 min-w-52 text-sm"
              placeholder="Hot lead: {{first_name}} {{last_name}} just hit the score threshold"
              value={c.message ?? ""}
              onChange={(e) => patchConfig({ message: e.target.value })}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Lands as an in-app notification and an email to that person. {TOKEN_HINT}
          </p>
        </>
      )}
    </div>
  );
};

const StepsEditor = ({
  steps,
  onChange,
  deps,
  depth = 0,
}: {
  steps: WorkflowStep[];
  onChange: (next: WorkflowStep[]) => void;
  deps: StepEditorDeps;
  depth?: number;
}) => {
  const replace = (i: number, next: WorkflowStep) =>
    onChange(steps.map((s, idx) => (idx === i ? next : s)));
  const remove = (i: number) => onChange(steps.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-2">
      {steps.map((step, i) =>
        step.type === "branch" ? (
          <div
            key={step.id}
            className="flex flex-col gap-2 rounded-md border border-dashed border-[#b07d3f]/60 p-2.5"
          >
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <GitBranch className="w-4 h-4 text-[#b07d3f]" />
              <span className="text-xs font-medium uppercase tracking-wide text-[#b07d3f]">
                If
              </span>
              <ConditionEditor
                value={step.condition}
                onChange={(condition) => replace(i, { ...step, condition })}
                sales={deps.sales}
                tags={deps.tags}
                datalistId={`cond-tags-${step.id}`}
              />
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto"
                aria-label="Remove branch"
                onClick={() => remove(i)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {(["then", "otherwise"] as const).map((arm) => (
                <div
                  key={arm}
                  className="rounded-md border border-border/70 p-2 flex flex-col gap-2 bg-muted/20"
                >
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {arm === "then" ? "Yes →" : "No →"}
                  </p>
                  <StepsEditor
                    steps={step[arm]}
                    onChange={(next) => replace(i, { ...step, [arm]: next })}
                    deps={deps}
                    depth={depth + 1}
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Both paths rejoin whatever comes after this branch.
            </p>
          </div>
        ) : step.type === "delay" ? (
          <div
            key={step.id}
            className="flex items-center gap-2 rounded-md border border-dashed border-border p-2.5 text-sm"
          >
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Wait
            </span>
            <Input
              type="number"
              min={1}
              className="h-7 w-16 text-xs"
              value={step.days}
              onChange={(e) =>
                replace(i, { ...step, days: Math.max(1, Number(e.target.value) || 1) })
              }
            />
            <span className="text-xs text-muted-foreground">
              day{step.days === 1 ? "" : "s"}, then continue
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto"
              aria-label="Remove wait"
              onClick={() => remove(i)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <ActionCard
            key={step.id}
            action={step}
            onChange={(next) => replace(i, next)}
            onRemove={() => remove(i)}
            deps={deps}
          />
        ),
      )}
      <div className="flex flex-wrap gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className={depth > 0 ? "h-7 text-xs" : "self-start"}
          onClick={() => onChange([...steps, newAction()])}
        >
          <Plus className="w-3.5 h-3.5" /> Add action
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={depth > 0 ? "h-7 text-xs" : "self-start"}
          onClick={() => onChange([...steps, newDelay()])}
        >
          <Clock className="w-3.5 h-3.5" /> Add wait
        </Button>
        {depth < MAX_BRANCH_DEPTH && (
          <Button
            variant="outline"
            size="sm"
            className={depth > 0 ? "h-7 text-xs" : "self-start"}
            onClick={() => onChange([...steps, newBranch()])}
          >
            <GitBranch className="w-3.5 h-3.5" /> Add if/then branch
          </Button>
        )}
      </div>
    </div>
  );
};

// --- Flow canvas (same visual language as sequences) ----------------------

const FlowNode = ({
  label,
  sub,
  kind,
}: {
  label: string;
  sub?: string;
  kind: "trigger" | "action" | "end" | "branch";
}) => (
  <div
    className={`rounded border px-3 py-2 text-sm w-56 ${
      kind === "trigger" || kind === "end"
        ? "bg-secondary border-border"
        : kind === "branch"
          ? "bg-muted border-dashed border-[#b07d3f]/60"
          : "bg-card border-border"
    }`}
  >
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
      {kind}
    </p>
    <p className="font-medium leading-tight">{label}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const FlowArrow = () => (
  <div className="flex flex-col items-center">
    <div className="w-px h-4 bg-border" />
    <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-border" />
  </div>
);

/** Recursive canvas: actions stack vertically; a branch splits into two columns that rejoin. */
const FlowSteps = ({
  steps,
  describe,
  sales,
}: {
  steps: WorkflowStep[];
  describe: (a: WorkflowAction) => string;
  sales: Sale[];
}) => (
  <>
    {steps.map((step) =>
      step.type === "branch" ? (
        <div key={step.id} className="flex flex-col items-center max-w-full">
          <FlowArrow />
          <FlowNode kind="branch" label={`If ${describeCondition(step.condition, sales)}?`} />
          <div className="flex gap-4 items-start mt-1 overflow-x-auto max-w-full">
            {(["then", "otherwise"] as const).map((arm) => (
              <div key={arm} className="flex flex-col items-center min-w-56">
                <p
                  className={`text-[10px] font-medium uppercase tracking-wide ${
                    arm === "then" ? "text-[#5d8a54]" : "text-muted-foreground"
                  }`}
                >
                  {arm === "then" ? "yes" : "no"}
                </p>
                {step[arm].length === 0 ? (
                  <p className="text-[11px] text-muted-foreground mt-1">(continue)</p>
                ) : (
                  <FlowSteps steps={step[arm]} describe={describe} sales={sales} />
                )}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">paths rejoin</div>
        </div>
      ) : step.type === "delay" ? (
        <div key={step.id} className="flex flex-col items-center">
          <FlowArrow />
          <FlowNode
            kind="action"
            label={`Wait ${step.days} day${step.days === 1 ? "" : "s"}`}
            sub="the enrollment holds, then continues"
          />
        </div>
      ) : (
        <div key={step.id} className="flex flex-col items-center">
          <FlowArrow />
          <FlowNode kind="action" label={actionDef(step.type).label} sub={describe(step)} />
        </div>
      ),
    )}
  </>
);

// --- Assignee routing (shared by tasks, owner changes, notifications) ------

export const AssigneePicker = ({
  value,
  onChange,
  sales,
  allowOwner = true,
  ownerLabel = "Contact's owner",
}: {
  value: AssigneeRule | undefined;
  onChange: (rule: AssigneeRule) => void;
  sales: Sale[];
  allowOwner?: boolean;
  ownerLabel?: string;
}) => {
  const rule: AssigneeRule = value ?? { mode: allowOwner ? "owner" : "user" };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={rule.mode}
        onValueChange={(mode: AssigneeRule["mode"]) => onChange({ ...rule, mode })}
      >
        <SelectTrigger className="h-8 w-44 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          {allowOwner && <SelectItem value="owner">{ownerLabel}</SelectItem>}
          <SelectItem value="user">Specific person</SelectItem>
          <SelectItem value="round_robin">Round-robin</SelectItem>
        </SelectContent>
      </Select>
      {rule.mode === "user" && (
        <Select
          value={rule.userId ?? ""}
          onValueChange={(userId) => onChange({ ...rule, userId })}
        >
          <SelectTrigger className="h-8 w-44 text-sm"><SelectValue placeholder="who…" /></SelectTrigger>
          <SelectContent>
            {sales.map((s) => (
              <SelectItem key={String(s.id)} value={String(s.id)}>
                {s.first_name} {s.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {rule.mode === "round_robin" && (
        <div className="flex flex-wrap items-center gap-1">
          {sales.map((s) => {
            const id = String(s.id);
            const inPool = (rule.poolIds ?? []).includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() =>
                  onChange({
                    ...rule,
                    poolIds: inPool
                      ? (rule.poolIds ?? []).filter((x) => x !== id)
                      : [...(rule.poolIds ?? []), id],
                  })
                }
                className={`rounded-full border px-2 py-0.5 text-xs ${
                  inPool
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                {s.first_name} {s.last_name?.[0] ?? ""}
              </button>
            );
          })}
          <span className="text-[11px] text-muted-foreground">
            {(rule.poolIds ?? []).length === 0 ? "no picks = rotate through everyone" : "rotates in order"}
          </span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

/** How long to wait after the last edit before writing to the database. */
const PERSIST_DEBOUNCE_MS = 600;

export const WorkflowsTab = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const workflows = config.workflows ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const workflow = workflows.find((w) => w.id === selectedId) ?? null;
  const workflowToDelete = workflows.find((w) => w.id === confirmDeleteId) ?? null;
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: tags = [] } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 100 },
  });

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row is debounced rather than split immediate-vs-blur like
  // the other builders - StepsEditor's action editor nests arbitrarily deep
  // (branches within branches), so there's no single set of "continuous
  // typing" call sites to opt out of persisting. A debounce handles every
  // caller uniformly without threading a persistNow flag through that whole
  // tree. flushPersist() (below) sends a still-pending edit immediately
  // when the user navigates away, so nothing is lost to the debounce
  // window. Without any of this, workflows only ever lived in the
  // browser's local store - never actually reaching the database.
  const pendingPersist = useRef<{
    timer: ReturnType<typeof setTimeout>;
    config: ConfigurationContextValue;
  } | null>(null);
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update workflows.", {
        type: "error",
      });
    }
  };
  const schedulePersist = (next: ConfigurationContextValue) => {
    if (pendingPersist.current) clearTimeout(pendingPersist.current.timer);
    const timer = setTimeout(() => {
      pendingPersist.current = null;
      void persist(next);
    }, PERSIST_DEBOUNCE_MS);
    pendingPersist.current = { timer, config: next };
  };
  const flushPersist = () => {
    if (!pendingPersist.current) return;
    clearTimeout(pendingPersist.current.timer);
    const next = pendingPersist.current.config;
    pendingPersist.current = null;
    void persist(next);
  };
  // Kept current every render so the unmount effect below always flushes
  // the latest pending edit, without re-running that effect on every render.
  const flushPersistRef = useRef(flushPersist);
  flushPersistRef.current = flushPersist;
  // Navigating to a different tab entirely (not just back to the list
  // view) unmounts this component - flush so that doesn't drop the change.
  useEffect(() => () => flushPersistRef.current(), []);

  const saveWorkflows = (next: WorkflowDef[]) => {
    const nextConfig = { ...config, workflows: next };
    updateConfig(nextConfig);
    schedulePersist(nextConfig);
  };
  const patchWorkflow = (patch: Partial<WorkflowDef>) => {
    if (!workflow) return;
    saveWorkflows(
      workflows.map((w) => (w.id === workflow.id ? { ...w, ...patch } : w)),
    );
  };
  const deleteWorkflow = (id: string) => {
    const remaining = workflows.filter((w) => w.id !== id);
    saveWorkflows(remaining);
    setConfirmDeleteId(null);
    // Only leave the detail view if the workflow being deleted is the one
    // currently open - deleting from the list view stays on the list.
    if (selectedId === id) setSelectedId(null);
  };
  const trigger = workflow ? triggerDef(workflow.trigger.type) : null;

  const paramInput = (
    kind: string | undefined,
    value: string,
    onChange: (v: string) => void,
  ) => {
    switch (kind) {
      case "stage":
        return (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="h-8 w-40 text-sm"><SelectValue placeholder="stage…" /></SelectTrigger>
            <SelectContent>
              {config.dealStages.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "days":
      case "threshold":
        return (
          <Input
            type="number"
            className="h-8 w-24 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      default:
        return (
          <Input
            className="h-8 w-44 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  const describeAction = (a: WorkflowAction): string => {
    const v = a.params?.value ?? "";
    const c = a.config ?? {};
    switch (a.type) {
      case "adjust_score":
        return `${Number(v) >= 0 ? "+" : ""}${v || "?"} points`;
      case "set_owner":
        return `to ${assigneeLabel(c.assignee ?? { mode: "user" }, sales)}${c.onlyIfUnowned ? " (only if unowned)" : ""}`;
      case "add_tag":
        return v || "pick a tag";
      case "enroll_sequence": {
        return (config.sequences ?? []).find((s) => s.id === v)?.name ?? "pick a sequence";
      }
      case "create_task":
        return `${v || "task title"} → ${assigneeLabel(c.assignee, sales)} · due in ${c.dueInDays ?? 0}d`;
      case "send_email": {
        const t = (config.emailTemplates ?? []).find((x) => x.id === v)?.name ?? "pick a template";
        return t;
      }
      case "notify_user":
        return `${assigneeLabel(c.assignee ?? { mode: "user" }, sales)}: ${c.message || "write the note"}`;
    }
  };

  // Live workspaces read the worker's real workflow enrollments (round 43);
  // the demo preview keeps its simulation.
  const liveRuns = useLiveRuns("workflow");
  const runsFor = (w: WorkflowDef) => {
    if (!liveRuns) return simulateWorkflowRuns(w, contacts);
    const mine = liveRuns.filter((r) => r.definitionId === w.id);
    const cutoff = Date.now() - 30 * 86_400_000;
    return {
      runs: mine.map((r) => ({ contactId: r.contactId, name: r.name, at: r.enrolledAt })),
      last30: mine.filter((r) => new Date(r.enrolledAt).getTime() >= cutoff).length,
      lastAt: mine[0]?.enrolledAt,
    };
  };

  // ------------------------------------------------------ list view ----
  if (!workflow) {
    return (
      <div className="flex flex-col gap-4">
        <Confirm
          isOpen={confirmDeleteId != null}
          title="Delete this workflow?"
          content={`"${workflowToDelete?.name ?? ""}" will stop running and can't be recovered.`}
          confirm="Delete"
          confirmColor="warning"
          onConfirm={() => confirmDeleteId && deleteWorkflow(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground flex-1">
            What each automation watches for and how often it fires - click a
            workflow to see its flow.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const id = `wf-${nextUid()}`;
              saveWorkflows([
                ...workflows,
                {
                  id,
                  name: "New workflow",
                  enabled: true,
                  trigger: { type: "form_submitted" },
                  actions: [
                    { id: nextUid(), type: "adjust_score", params: { value: "10" } },
                  ],
                },
              ]);
              setSelectedId(id);
            }}
          >
            <Plus className="w-4 h-4" /> New workflow
          </Button>
        </div>

        {workflows.length > 0 && (
          <Card>
            <CardContent className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Workflow</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                    <th className="py-1.5 pr-3 font-medium">Trigger</th>
                    <th className="py-1.5 pr-3 font-medium">Actions</th>
                    <th className="py-1.5 pr-3 font-medium">Runs (30d)</th>
                    <th className="py-1.5 pr-3 font-medium">Last run</th>
                    <th className="py-1.5 font-medium" />
                    <th className="py-1.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {workflows.map((w) => {
                    const r = runsFor(w);
                    return (
                      <tr
                        key={w.id}
                        className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => setSelectedId(w.id)}
                      >
                        <td className="py-2 pr-3 font-medium">{w.name}</td>
                        <td className="py-2 pr-3">
                          <span
                            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                              w.enabled
                                ? "bg-[#8fb586] text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {w.enabled ? "on" : "off"}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {triggerDef(w.trigger.type).label}
                          {w.trigger.params?.value ? `: ${w.trigger.params.value}` : ""}
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                          {flattenWorkflowActions(w.actions).length}
                          {w.actions.some((s) => s.type === "branch") && (
                            <GitBranch className="w-3 h-3 inline ml-1 text-muted-foreground" />
                          )}
                        </td>
                        <td className="py-2 pr-3 tabular-nums font-semibold">{r.last30}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {shortDate(r.lastAt)}
                        </td>
                        <td className="py-2 pr-1 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label={`Delete ${w.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(w.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                        <td className="py-2 text-right">
                          <ArrowRight className="w-3.5 h-3.5 inline text-muted-foreground" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">
                {liveRuns
                  ? "Live runs from this workspace's automation worker."
                  : "Run counts are sample data in the preview - live workspaces show the worker's real runs."}{" "}
                <Link
                  to="/help/workflows-and-lead-scoring"
                  className="underline hover:no-underline"
                >
                  How workflows and scoring work
                </Link>
              </p>
            </CardContent>
          </Card>
        )}

        {workflows.length === 0 && (
        <EmptyState
          icon={Zap}
          title="Automate the busywork"
          body="Workflows watch for a trigger - a form submission, a stage change, a score threshold, silence - and act: bump the score, reassign the owner, enroll a sequence, raise a task. Set them once and the follow-through stops depending on memory."
          primaryLabel="Start with 'gone quiet → task'"
          onPrimary={() => {
            const id = `wf-${nextUid()}`;
            saveWorkflows([
              ...workflows,
              {
                id,
                name: "Gone quiet → follow-up task",
                enabled: true,
                trigger: { type: "no_activity", params: { value: "30" } },
                actions: [
                  {
                    id: nextUid(),
                    type: "create_task",
                    params: { value: "Re-engage - no activity in 30 days" },
                  },
                  { id: nextUid(), type: "adjust_score", params: { value: "-10" } },
                ],
              },
            ]);
            setSelectedId(id);
          }}
          secondaryLabel="Start blank"
          onSecondary={() => {
            const id = `wf-${nextUid()}`;
            saveWorkflows([
              ...workflows,
              {
                id,
                name: "New workflow",
                enabled: true,
                trigger: { type: "form_submitted" },
                actions: [
                  { id: nextUid(), type: "adjust_score", params: { value: "10" } },
                ],
              },
            ]);
            setSelectedId(id);
          }}
          footnote="Definitions save per tenant now; the automation engine executes them live at deployment."
          />
        )}

        {/* ---------------------------------------------- scoring ------ */}
        <ScoringRulesCard />
      </div>
    );
  }

  // ---------------------------------------------------- detail view ----
  const runs = runsFor(workflow);
  return (
    <div className="flex flex-col gap-4">
      <Confirm
        isOpen={confirmDeleteId != null}
        title="Delete this workflow?"
        content={`"${workflowToDelete?.name ?? ""}" will stop running and can't be recovered.`}
        confirm="Delete"
        confirmColor="warning"
        onConfirm={() => confirmDeleteId && deleteWorkflow(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            flushPersist();
            setSelectedId(null);
          }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All workflows
        </button>
        <h2 className="text-base font-semibold flex-1 truncate">{workflow.name}</h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {runs.last30} runs in the last 30 days
        </span>
      </div>

      {trigger && (
        <div className="grid xl:grid-cols-2 gap-4 items-start">
          <Card>
            <CardContent className="p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  className="w-56 font-medium"
                  value={workflow.name}
                  onChange={(e) => patchWorkflow({ name: e.target.value })}
                />
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Switch
                    checked={workflow.enabled}
                    onCheckedChange={(enabled) => patchWorkflow({ enabled })}
                  />
                  {workflow.enabled ? "On" : "Off"}
                </label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-auto"
                  aria-label="Delete workflow"
                  onClick={() => setConfirmDeleteId(workflow.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Zap className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">When</span>
                <Select
                  value={workflow.trigger.type}
                  onValueChange={(type: WorkflowDef["trigger"]["type"]) =>
                    patchWorkflow({ trigger: { type, params: {} } })
                  }
                >
                  <SelectTrigger className="h-8 w-56 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIGGERS.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {trigger.paramLabel && (
                  <>
                    <span className="text-muted-foreground">{trigger.paramLabel.toLowerCase()}</span>
                    {paramInput(
                      trigger.paramKind,
                      workflow.trigger.params?.value ?? "",
                      (value) =>
                        patchWorkflow({
                          trigger: { ...workflow.trigger, params: { value } },
                        }),
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Then
                </p>
                <StepsEditor
                  steps={workflow.actions}
                  onChange={(actions) => patchWorkflow({ actions })}
                  deps={{ sales, tags, crmConfig: config }}
                />
              </div>

              <p className="text-[11px] text-muted-foreground border-t border-border pt-2">
                Definitions save per tenant now; the automation engine executes
                them live at deployment, reading triggers off the event spine.
              </p>
            </CardContent>
          </Card>

          {/* Visual flow */}
          <Card>
            <CardContent className="p-4 flex flex-col items-center">
              <FlowNode
                kind="trigger"
                label={trigger.label}
                sub={
                  trigger.paramLabel && workflow.trigger.params?.value
                    ? `${trigger.paramLabel}: ${workflow.trigger.params.value}`
                    : undefined
                }
              />
              <FlowSteps
                steps={workflow.actions}
                describe={describeAction}
                sales={sales}
              />
              <FlowArrow />
              <FlowNode
                kind="end"
                label={workflow.enabled ? "Workflow armed" : "Workflow off"}
                sub={workflow.enabled ? "runs on every match" : "toggle on to arm"}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Recent runs
          </p>
          {runs.runs.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {workflow.enabled
                ? "No matches yet - runs appear here as contacts hit the trigger."
                : "Workflow is off - toggle it on to start matching."}
            </p>
          ) : (
            <>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-2 font-medium">Contact</th>
                      <th className="py-1 pr-2 font-medium">When</th>
                      <th className="py-1 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.runs.slice(0, 40).map((r) => (
                      <tr key={String(r.contactId)} className="border-t border-border">
                        <td className="py-1.5 pr-2">
                          <Link
                            to={`/contacts/${r.contactId}/show`}
                            className="underline hover:no-underline"
                          >
                            {r.name}
                          </Link>
                        </td>
                        <td className="py-1.5 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                          {shortDate(r.at)}
                        </td>
                        <td className="py-1.5 text-xs text-muted-foreground">
                          {(() => {
                            const n = flattenWorkflowActions(workflow.actions).length;
                            const branched = workflow.actions.some((s) => s.type === "branch");
                            return `${branched ? "up to " : ""}${n} action${n === 1 ? "" : "s"} executed`;
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {runs.runs.length > 40 && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Showing 40 of {runs.runs.length} runs.
                </p>
              )}
            </>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">
            Sample data in the preview - the live engine logs every execution
            with its trigger event and each action's outcome.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

/** Native lead-scoring rules - the signals workflows and Hot Contacts feed on. */
const ScoringRulesCard = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const stored = config.scoringRules ?? [];
  // Merge with defaults so new rules appear for older stored configs
  const rules: ScoringRule[] = [
    ...stored,
    ...defaultScoringRules.filter((d) => !stored.some((r) => r.key === d.key)),
  ];

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for the on/off toggle and on blur
  // for the free-typed points value. Without this, scoring rules only ever
  // lived in the browser's local store - never actually reaching the
  // database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update scoring rules.", {
        type: "error",
      });
    }
  };
  const save = (next: ScoringRule[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, scoringRules: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushScoringRules = () => void persist({ ...config, scoringRules: rules });

  return (
    <Card>
      <CardHeader className="px-4 pb-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <Gauge className="w-4 h-4 text-muted-foreground" />
          Lead scoring
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pt-2 flex flex-col gap-1">
        <p className="text-xs text-muted-foreground mb-1 max-w-2xl">
          Every contact gets a 0–100 score from these signals - it ranks Hot
          Contacts, powers the "Keep an eye on" panel, and feeds
          score-triggered workflows. Tune the points to your funnel.
        </p>
        <div className="grid md:grid-cols-2 gap-x-8">
          {rules.map((r) => (
            <div
              key={r.key}
              className="flex items-center gap-2 py-1.5 border-t border-border text-sm"
            >
              <Switch
                checked={r.enabled !== false}
                onCheckedChange={(enabled) =>
                  save(rules.map((x) => (x.key === r.key ? { ...x, enabled } : x)))
                }
              />
              <span className={r.enabled === false ? "text-muted-foreground/60" : ""}>
                {r.label}
              </span>
              <Input
                type="number"
                className="h-7 w-16 ml-auto text-sm tabular-nums"
                value={r.points}
                onBlur={flushScoringRules}
                onChange={(e) =>
                  save(
                    rules.map((x) =>
                      x.key === r.key
                        ? { ...x, points: Number(e.target.value) || 0 }
                        : x,
                    ),
                    { persistNow: false },
                  )
                }
              />
              <span className="text-xs text-muted-foreground">pts</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
